<template>
  <v-div class="Comment" relative mb-5>
    <v-div
      border-softened-right-thin
      border-softened-top-thin
      border-softened-bottom-thin
    >
      <v-div border-primary-left-verythick>
        <v-div pt-6 pl-8 overflow-hidden background-subdued>
          <div
            :id="`comment-header-${comment.number}`"
            role="heading"
            :aria-level="headingLevel"
          >
            <span class="d-sr-only">{{ srText }}</span>
            <span aria-hidden="true">
              <v-div clear-fix py-3>
                <v-div v-if="!isRemoved" ml-4>
                  <v-div subtitle-1 font-weight-bold>{{
                    comment.author | displayName
                  }}</v-div>
                </v-div>
                <v-div ml-4>
                  <v-div font-weight-regular>{{
                    comment.createdAt | localDateTime
                  }}</v-div>
                </v-div>
              </v-div>
              <v-row>
                <v-div absolute right="4" top="4" pa-2 overflow-hidden>
                  <v-chip outlined>
                    {{ comment.number }}
                  </v-chip>
                </v-div>
              </v-row>
            </span>
          </div>
          <v-div v-if="isRemoved" ml-4 mb-2
            >This comment has been removed by the moderator.</v-div
          >
        </v-div>
        <v-card-text v-if="!isRemoved" pt-6 pl-8 overflow-hidden>
          <p>{{ comment.content }}</p>
          <v-div v-if="allowReporting && articleDetailsAllowReporting">
            <v-div v-if="isLoggedIn">
              <v-btn
                :id="`comment-report-${comment.number}`"
                data-test-id="reportCommentButton"
                small
                outlined
                rounded
                :aria-labelledby="`comment-report-${comment.number} comment-header-${comment.number}`"
                @click="reportLoggedIn"
              >
                <span v-if="displayIsDesktop">Report Comment</span>
                <v-icon v-else color="muted">{{ mdiFlag }}</v-icon>
              </v-btn>
              <v-btn
                :id="`comment-reply-${comment.number}`"
                data-test-id="replyButton"
                small
                outlined
                rounded
                :aria-labelledby="`comment-reply-${comment.number} comment-header-${comment.number}`"
                @click="showReplyField"
              >
                <span v-if="displayIsDesktop">Reply</span>
                <v-icon v-else>{{ mdiReply }}</v-icon>
              </v-btn>
            </v-div>
            <VueRecaptcha
              v-else
              ref="recaptcha"
              :sitekey="recaptchaSiteKey"
              size="invisible"
              badge="right"
              @verify="onVerify"
              @expired="onExpired"
            >
              <v-btn
                :id="`comment-report-${comment.number}`"
                :ref="`comment-report-${comment.number}`"
                data-test-id="reportCommentButton"
                small
                outlined
                rounded
                :aria-labelledby="`comment-report-${comment.number} comment-header-${comment.number}`"
                @click="reportRequested"
              >
                <span v-if="displayIsDesktop">Report Comment</span>
                <v-icon v-else color="muted">{{ mdiFlag }}</v-icon>
              </v-btn>
            </VueRecaptcha>
          </v-div>
        </v-card-text>
      </v-div>
    </v-div>
    <v-div v-if="!isRemoved" :style="commentIndentation">
      <v-div
        v-if="replyFieldVisible"
        pa-4
        border-softened-right-thin
        border-softened-left-thin
        border-softened-bottom-thin
      >
        <AddComment :is-reply="true" @postComment="postArticleReply" />
      </v-div>
      <CommentReply
        v-for="reply in replies"
        :key="reply.id"
        :comment="reply"
        :allow-reporting="allowReporting"
        :recaptcha-site-key="recaptchaSiteKey"
        :show-removed-details="showRemovedDetails"
      />
    </v-div>
  </v-div>
</template>

<script>
import VueRecaptcha from "vue-recaptcha";
import { mapState } from "vuex";
import { mdiFlag, mdiReply } from "@mdi/js";
import mapFlags from "~/plugins/utilities/mapFlags";

import {
  displayIsMobile,
  displayIsTablet,
  displayIsDesktop,
} from "~/components/utilities/displayBreakpoints";
import isLoggedIn from "~/plugins/utilities/isLoggedIn";
import CommentReply from "~/components/comments/CommentReply";
import AddComment from "~/components/comments/AddComment.vue";
import displayName from "~/plugins/utilities/displayName";
import localDateTime from "~/plugins/utilities/localDateTime";
import commentReport from "~/actions/tasks/commentReport";

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Comment",
  components: {
    VueRecaptcha,
    CommentReply,
    AddComment,
  },
  props: {
    article: { type: Object, required: false, default: () => {} },
    comment: { type: Object, required: true },
    allowReporting: { type: Boolean, default: true },
    headingLevel: { type: Number, default: 3 },
    recaptchaSiteKey: { type: String, required: false, default: "" },
    showRemovedDetails: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: true,
      replyFieldVisible: false,
      // Icons
      mdiFlag,
      mdiReply,
    };
  },

  computed: {
    displayIsMobile,
    displayIsTablet,
    displayIsDesktop,
    ...mapState({
      commentReportReasons: (state) =>
        state.referenceData.commentReportReasonTypes,
      user: (state) => state.user,
    }),
    ...mapFlags(["articleDetailsAllowReporting"]),
    isLoggedIn() {
      return isLoggedIn(this.user);
    },
    isRemoved() {
      return this.comment.status === "REMOVED" && !this.showRemovedDetails;
    },
    srText() {
      return `Comment number ${this.comment.number}, ${
        displayName(this.comment.author) ||
        "This comment has been removed by the moderator."
      }: ${localDateTime(this.comment.createdAt)}`;
    },
    replies() {
      return this.comment.replies;
    },
    commentIndentation() {
      return !this.displayIsMobile
        ? { marginLeft: "50px" }
        : { marginLeft: "8px" };
    },
  },
  methods: {
    commentReport,
    reportRequested() {
      this.$refs.recaptcha.execute();
    },
    onVerify(response) {
      const currentTarget =
        this.$refs[`comment-report-${this.comment.number}`].$el;
      this.report(response, currentTarget);
    },
    onExpired() {
      this.$refs.recaptcha.reset();
    },
    reportLoggedIn({ currentTarget }) {
      this.report(null, currentTarget);
    },
    report(captchaResponse, currentTarget) {
      this.$root.$emit("show-report", {
        title: "Report Comment",
        reportContent: this.comment.content,
        reportContentId: this.comment.id,
        text: "Please let us know how you think this comment contravenes our Commenting and Discussion Policy:",
        options: this.commentReportReasons,
        action: this.commentReport,
        captchaResponse,
        currentTarget,
      });

      this.$refs.recaptcha?.reset();
    },
    showReplyField() {
      this.replyFieldVisible = true;
    },
    postArticleReply(content) {
      this.$store.dispatch("postArticleComment", {
        content,
        articleId: this.article.id,
        partner: this.article.origin,
        parentCommentId: this.comment.id,
      });
      this.replyFieldVisible = false;
    },
  },
};
</script>

<style>
.grecaptcha-badge {
  visibility: hidden;
}
</style>
